import type { MonsterDef } from '../../sim/registry';
import type { StatusApply } from '../../sim/skillTypes';
import { circle, cone, ring } from '../classes/kit';
import { monsterStats } from '../balance';
import { mAreas, mBreath, mCombo, mLeap, mLunge, mMelee, mRoar, mSupport, mVolley } from './kit';

/**
 * Frosthollow (field, levels 21–30): the frozen dead, wraiths, wolf packs,
 * yetis and the field boss Rimefang Alpha. `frost_ghoul` and `ice_wraith` also
 * haunt Frostfang Keep. Rimefang's script: src/content/bosses/story.ts.
 */

const ICE = 0x9fe8ff;
const chill = (duration: number): StatusApply => ({ id: 'chill', duration });
const freeze = (duration: number): StatusApply => ({ id: 'freeze', duration });

export const FROSTHOLLOW_MONSTERS: MonsterDef[] = [
  {
    id: 'frost_ghoul', name: 'Frost Ghoul', grade: 'normal', model: 'mob_ghoul', scale: 1, radius: 0.5, tint: 0x9fc8e8, creature: 'Undead',
    stats: (l) => monsterStats(l, 'normal', { speed: 1.1 }),
    skills: [
      mMelee('fghoul_bite', 'Frostbite', { shape: cone(2.1, 60), coef: 1.1, windup: 0.35, cd: 1.8 }),
      mCombo('fghoul_claws', 'Rime Claws', {
        hits: [
          { at: 0.35, shape: cone(2.3, 80), coef: 0.6 },
          { at: 0.62, shape: cone(2.3, 80), coef: 0.8, status: [chill(2)] },
        ],
        cd: 4, element: 'water',
      }),
      mLunge('fghoul_pounce', 'Grave Pounce', { distance: 5, coef: 1.4, cd: 7, range: [2.5, 6] }),
    ],
    brain: 'melee', aggroRange: 10, leash: 22, xp: 68, loot: 'normal', weakness: 'fire',
  },
  {
    id: 'ice_wraith', name: 'Ice Wraith', grade: 'normal', model: 'mob_wraith', scale: 1, radius: 0.5, creature: 'Spirit',
    stats: (l) => monsterStats(l, 'normal', { hp: 0.8 }),
    skills: [
      mVolley('wraith_lance', 'Ice Lance', { coef: 1.3, speed: 18, reach: 15, windup: 0.6, cd: 2.4, status: [chill(2)], element: 'water', hue: ICE, anim: 'Cast' }),
      // Hard CC: blue circle under the target.
      mAreas('wraith_freeze', 'Freezing Circle', {
        steps: [{ shape: circle(3), anchor: 'point', delay: 1.2, coef: 1, telegraph: 'blue', reaction: { kind: 'flinch' }, status: [freeze(1.5)], element: 'water' }],
        cd: 9, range: [0, 13], maxRange: 14, anim: 'Cast', hue: ICE,
      }),
      // Pushes away players who close in.
      mAreas('wraith_nova', 'Frost Nova', {
        steps: [{ shape: circle(3.5), delay: 0.7, coef: 1.1, telegraph: 'yellow', reaction: { kind: 'knockback', power: 3 }, status: [chill(2)], element: 'water' }],
        cd: 8, range: [0, 3], anim: 'Cast', hue: ICE,
      }),
    ],
    brain: 'caster', aggroRange: 13, leash: 24, xp: 70, loot: 'normal', weakness: 'fire',
  },
  {
    id: 'frost_wolf', name: 'Frost Wolf', grade: 'normal', model: 'mob_wolf', scale: 1.05, radius: 0.6, tint: 0xeef6ff, creature: 'Beast',
    stats: (l) => monsterStats(l, 'normal', { hp: 0.9, speed: 1.2 }),
    skills: [
      mMelee('fwolf_bite', 'Frost Fang', { shape: cone(2.2, 70), coef: 1.1, windup: 0.35, cd: 1.8 }),
      mLunge('fwolf_pounce', 'Pounce', { distance: 4.5, coef: 1.5, windup: 0.5, cd: 7, range: [2.5, 6] }),
      mSupport('fwolf_howl', 'Pack Howl', { radius: 8, status: [{ id: 'atkUp', duration: 8, value: 0.15 }], cd: 20, range: [0, 10], anim: 'Roar', hue: ICE }),
    ],
    brain: 'melee', aggroRange: 10, leash: 24, xp: 60, loot: 'vale_beast', weakness: 'fire',
  },
  {
    id: 'frost_yeti', name: 'Frost Yeti', grade: 'elite', model: 'mob_yeti', scale: 1, radius: 1.1, creature: 'Beast',
    stats: (l) => monsterStats(l, 'elite', { hp: 1.1, speed: 0.95 }),
    skills: [
      mCombo('yeti_pummel', 'Pummel', {
        hits: [
          { at: 0.5, shape: cone(3, 90), coef: 0.9 },
          { at: 0.8, shape: cone(3, 90), coef: 0.9 },
          { at: 1.2, shape: circle(3.2, 1.5), coef: 1.6, reaction: { kind: 'knockdown', duration: 1, power: 1.5 } },
        ],
        cd: 6, range: [0, 3], superArmor: 'paralysis',
      }),
      mAreas('yeti_avalanche', 'Avalanche Slam', {
        steps: [{ shape: circle(5), delay: 1.1, coef: 2.4, status: [chill(3)], element: 'water' }],
        cd: 10, range: [0, 4], superArmor: 'paralysis', hue: ICE,
      }),
      mVolley('yeti_ice_boulder', 'Ice Boulder', {
        coef: 2, speed: 14, reach: 16, radius: 1, explode: circle(2.6), windup: 0.9, status: [freeze(1.2)],
        cd: 9, range: [4, 15], superArmor: 'paralysis', anim: 'Attack2', hue: ICE,
      }),
      mBreath('yeti_breath', 'Frost Breath', { radius: 7, angle: 50, coef: 2.2, count: 5, interval: 0.2, windup: 0.8, status: [chill(2)], cd: 12, range: [0, 6], superArmor: 'paralysis', element: 'water', hue: ICE }),
    ],
    brain: 'melee', aggroRange: 11, leash: 24, xp: 220, loot: 'elite', weakness: 'fire',
  },
  {
    id: 'rimefang_alpha', name: 'Rimefang Alpha', grade: 'named', model: 'mob_wolf', scale: 2.4, radius: 1.5, tint: 0xcfe6ff, creature: 'Beast',
    title: 'Field Boss', stagger: 560, hpBars: 16, weakness: 'fire',
    stats: (l) => monsterStats(l, 'named', { hp: 3.2, speed: 1.15 }),
    skills: [
      mCombo('rime_maul', 'Mauling Fangs', {
        hits: [
          { at: 0.5, shape: cone(4, 90), coef: 1.2 },
          { at: 0.85, shape: cone(4.4, 100), coef: 1.6, step: 1, reaction: { kind: 'knockback', power: 2.5 } },
        ],
        cd: 4, range: [0, 4.5], superArmor: 'push',
      }),
      mLeap('rime_pounce', 'Glacial Pounce', { radius: 3.8, coef: 2.8, maxDistance: 14, land: 0.9, status: [chill(2)], cd: 8, range: [4, 14], superArmor: 'push', hue: ICE }),
      mBreath('rime_breath', 'Frost Breath', { radius: 10, angle: 60, coef: 3.2, count: 6, interval: 0.2, windup: 1, status: [chill(2)], cd: 12, range: [0, 9], superArmor: 'push', element: 'water', hue: ICE }),
      mAreas('rime_tail', 'Rime Tail', {
        steps: [{ shape: cone(5.5, 200, 180), delay: 0.7, coef: 2, telegraph: 'yellow', reaction: { kind: 'knockback', power: 3 } }],
        cd: 7, range: [0, 5], superArmor: 'push', anim: 'Attack2',
      }),
      // Summon: the boss script calls frost wolves when the howl lands (0.9 s).
      mRoar('rime_howl', 'Call of the Pack', {
        announce: 'Rimefang howls for the pack!', duration: 1.6, cd: 35, hue: ICE,
        nova: { shape: circle(6), delay: 0.9, coef: 0.6, telegraph: 'yellow', reaction: { kind: 'knockback', power: 3 }, status: [chill(2)] },
      }),
      // Mechanic (35%): freezing core, then the outer storm.
      mAreas('rime_winters_maw', "Winter's Maw", {
        announce: "Winter's Maw opens! Get out, then get back in!",
        steps: [
          { shape: circle(5), delay: 1.2, coef: 2.6, telegraph: 'blue', status: [freeze(2)], element: 'water' },
          { shape: ring(5, 14), delay: 2.3, coef: 3, element: 'water' },
        ],
        cd: 40, range: [0, 99], superArmor: 'full', anim: 'Roar', hue: ICE,
      }),
    ],
    brain: 'boss', aggroRange: 14, leash: 40, xp: 1600, loot: 'field_boss',
  },
];
